import {
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
} from "core/consts";
import { postPatientDocument } from "core/model/careseekers/patientDocumentTransfer";
import { bytesToBase64 } from "core/model/crypto";
import { b64DecodeUnicode } from "core/model/utils/base64";
import { getErrorMessage } from "core/model/utils/errors";
import {
  AHBMessageResponse,
  Careseeker,
  Patient,
  PatientDocumentType,
  QueryProgressActive,
} from "core/types";
import { useHL7ImportStatusContext } from "dsl/atoms/HL7ImportStatusContext";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useGetAHBResponse } from "onprem/hooks";
import { useState } from "react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import { detectSchemaVersion, getCachedXSL, transformXmlToPdf } from "./useAHBResponseToPDF";

const AHB_RESPONSE_PDF_PAGE_COUNT = 1;

export function useAHBResponseToHIS({
  careseekerName,
  externalId,
  userId,
}: {
  careseekerName: NonNullable<Careseeker["name"]>;
  externalId: NonNullable<Patient["external_id"]>;
  userId: NonNullable<Patient["user_id"]>;
}) {
  const toast = useToast();
  const { on_premise_authorization_token, on_premise_domain_ssl } =
    useHL7ImportStatusContext();
  const translations = useTranslations();
  const [progress, setProgress] = useState<QueryProgressActive>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const { trackEvent } = useTracking();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [, getAHBResponse] = useGetAHBResponse();

  const handleSendToHis = async ({
    documentType,
    response_id,
  }: {
    documentType: PatientDocumentType;
    response_id: AHBMessageResponse["response_id"];
  }) => {
    setLoadingId(response_id);
    setProgress(QUERY_PROGRESS_PENDING);

    try {
      if (!on_premise_domain_ssl) {
        throw new Error("'on_premise_domain_ssl' is required");
      }
      const base64Xml = await getAHBResponse(response_id);
      if (!base64Xml) {
        throw new Error("Could not fetch AHB response content");
      }

      const cleaned = base64Xml.trim().replace(/(^")|("$)/g, "");
      const xml = b64DecodeUnicode(cleaned);

      if (!xml) {
        throw new Error("Could not fetch AHB response content");
      }
      const version = detectSchemaVersion(xml);
      const xsl = await getCachedXSL(version);

      const pdfBytes = await transformXmlToPdf({
        endPage: AHB_RESPONSE_PDF_PAGE_COUNT,
        xml,
        xsl,
      });
      const encodedPdf = bytesToBase64(pdfBytes);

      await postPatientDocument({
        careseekerName,
        documentType,
        encodedPdf,
        externalId,
        on_premise_authorization_token,
        on_premise_domain_ssl,
        responseId: response_id,
        targetHTML: encodedPdf,
        trackEvent,
        userId,
      });
      toast({
        color: "success",
        message:
          translations.insuranceApp.eligibilityTab.replySection
            .sendToHisSuccess,
      });
    } catch (err) {
      getErrorMessage(err);
      toast({
        color: "danger",
        message:
          translations.insuranceApp.eligibilityTab.replySection.sendToHisError,
      });
    } finally {
      setProgress(QUERY_PROGRESS_NOT_STARTED);
    }
  };

  return [
    (response_id: AHBMessageResponse["response_id"]) =>
      response_id === loadingId ? progress : QUERY_PROGRESS_NOT_STARTED,
    handleSendToHis,
  ] as const;
}
